import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type SliderProps as MuiSliderProps } from "@mui/material";

import { Slider } from "../Slider";
import { Text } from "../Text";

/**
 * Props for the ProgressBar component.
 * @param completed - The number of completed items.
 * @param total - The total number of items.
 * @param showCount - Whether to show the count of completed items as a fraction.
 * @param countSuffix - A suffix to append to the count.
 * @param label - A label to display, which takes priority over the count.
 * @param color - The color of the progress bar.
 */
export interface ProgressBarProps {
  completed: number;
  total: number;
  showCount?: boolean;
  countSuffix?: string;
  label?: React.ReactNode;
  color?: MuiSliderProps["color"];
}

export function ProgressBar(props: ProgressBarProps) {
  const { completed, total, showCount = false, countSuffix, label, color = "primary" } = props;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        width: "100%",
      }}
      spacing={3}
    >
      <Box sx={{ flex: 1, display: "flex" }}>
        <Slider
          thumb={false}
          value={Math.round((completed / total) * 100)}
          color={color}
          sx={{
            width: "100%",
            paddingX: 0,
            cursor: "default",
            overflow: "hidden",
          }}
        />
      </Box>
      {isDefined(label) && <Text variant="body2">{label}</Text>}
      {isNil(label) &&
        showCount &&
        (isDefined(countSuffix) ? (
          <Text variant="body2">{`${completed} / ${total} ${countSuffix}`}</Text>
        ) : (
          <Text variant="body2">{`${completed} / ${total}`}</Text>
        ))}
    </Stack>
  );
}
